// eslint-disable-next-line
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Dropdown, Menu, Tooltip } from 'antd';
import { userSignOut } from 'appRedux/actions/Auth';
import imageDefault from 'assets/images/placeholder.jpg';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSignOutAlt,
  faKey,
  faCheckCircle,
  faCalendar,
  faMinus,
  faNewspaper,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { AppState } from 'ReducerState';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import IntlMessages from 'util/IntlMessages';
import { MdKeyboardArrowDown } from 'react-icons/md';
import cn from '@/utils/cn';
import { useAppStateStore } from '@/zusttandStore/appState.store';
import { RELEASE_START_DATE, releaseData } from '@/config/release.config';
import moment from 'moment';

// ── Inject CSS animations once (works with styled-components v4) ──────
const UI_STYLE_ID = 'userinfo-badge-styles';

function injectUserInfoStyles() {
  if (document.getElementById(UI_STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = UI_STYLE_ID;
  s.textContent = `
    @keyframes ui-pulse-ring {
      0%   { transform: scale(0.95); opacity: 0.75; }
      65%  { transform: scale(1.10); opacity: 0;    }
      100% { transform: scale(0.95); opacity: 0;    }
    }
    @keyframes ui-shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    @keyframes ui-badge-pop {
      0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
      55%  { transform: scale(1.25) rotate(4deg);  opacity: 1; }
      100% { transform: scale(1) rotate(0deg);     opacity: 1; }
    }
    @keyframes ui-badge-pulse {
      0%, 100% { box-shadow: 0 0 0 0   rgba(82,196,26,0.5); }
      50%       { box-shadow: 0 0 0 5px rgba(82,196,26,0);   }
    }
    /* Badge blink — runs indefinitely while visible (2 mins) */
    @keyframes ui-badge-blink {
      0%, 45%  { opacity: 1;   transform: scale(1);    }
      50%, 95% { opacity: 0.2; transform: scale(0.82); }
      100%     { opacity: 1;   transform: scale(1);    }
    }
    .ui-badge-blinking {
      animation: ui-badge-blink 1s ease-in-out infinite !important;
    }
    @keyframes ui-menu-blink {
      0%   { background: rgba(24,144,255,0.05); }
      30%  { background: rgba(24,144,255,0.22); box-shadow: inset 3px 0 0 #1890ff; }
      60%  { background: rgba(24,144,255,0.08); }
      100% { background: rgba(24,144,255,0.14); box-shadow: inset 3px 0 0 #1890ff; }
    }
    @keyframes ui-icon-glow {
      0%, 100% { box-shadow: 0 0 0   0px rgba(82,196,26,0.6); }
      50%       { box-shadow: 0 0 8px 4px rgba(82,196,26,0.4); }
    }

    /* Wrapper with pulsing ring */
    .ui-new-ring {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .ui-new-ring::before {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 9999px;
      background: linear-gradient(
        135deg,
        #1890ff 0%,
        #40a9ff 30%,
        #bae7ff 60%,
        #1890ff 100%
      );
      background-size: 200% 200%;
      z-index: 0;
      animation:
        ui-pulse-ring 2.2s ease-out infinite,
        ui-shimmer    3.5s linear    infinite;
    }
    .ui-new-ring > * {
      position: relative;
      z-index: 1;
    }

    /* NEW badge */
    .ui-new-badge {
      position: absolute;
      top: -9px;
      right: -9px;
      z-index: 20;
      background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
      color: #fff;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.6px;
      padding: 2px 5px;
      border-radius: 9999px;
      border: 1.5px solid #fff;
      line-height: 1.3;
      white-space: nowrap;
      user-select: none;
      pointer-events: none;
      animation:
        ui-badge-pop   0.45s cubic-bezier(0.22,1,0.36,1) both,
        ui-badge-pulse 1.8s  ease-in-out 0.5s infinite;
    }

    /* Highlighted version menu item (always — static style) */
    .ui-menu-version-new.ant-dropdown-menu-item {
      background: linear-gradient(90deg,
        rgba(24,144,255,0.10) 0%,
        rgba(64,169,255,0.04) 100%) !important;
      border-left: 3px solid #1890ff !important;
      padding-left: 17px !important;
    }
    .ui-menu-version-new.ant-dropdown-menu-item:hover {
      background: linear-gradient(90deg,
        rgba(24,144,255,0.22) 0%,
        rgba(64,169,255,0.14) 100%) !important;
    }
    /* Blink animation when dropdown just opened */
    .ui-menu-version-blinking.ant-dropdown-menu-item {
      animation: ui-menu-blink 0.9s ease-in-out 3 !important;
      border-left: 3px solid #1890ff !important;
      padding-left: 17px !important;
    }
    .ui-icon-glow {
      animation: ui-icon-glow 0.9s ease-in-out 3 !important;
    }
  `;
  document.head.appendChild(s);
}

// ── Styled menu (v4-compatible — no transient props) ─────────────────
const StyledUserMenu = styled(Menu)`
  border-radius: 12px;
  padding: 4px 0;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.2);

  .ant-dropdown-menu-item {
    font-weight: 500;
    font-size: 15px;
    color: #1677ff;
    padding: 10px 20px;
    transition:
      background 0.2s,
      color 0.2s;
    &:hover {
      background-color: #cceeff;
      color: #0958d9;
    }
  }
`;

// ── Component ─────────────────────────────────────────────────────────
const UserInfo = () => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [blinking, setBlinking] = useState(false);

  const state = useSelector((state: AppState) => state);
  const AuthUser = state.auth;
  const authUser = (AuthUser as any).authUser ?? {};
  const systemConfig = state.systemConfig;
  const markLocations = state.currentLocation.markLocations;

  const profile_pic = authUser?.profile_pic ?? '';
  const user_name = authUser?.user_name;
  const current_shift = authUser?.current_shift;
  const login_time = authUser?.login_time;

  const {
    dataSource: { SYSTEM },
  } = systemConfig;
  const SYSTEM_DATE = SYSTEM?.SYSTEM_DATE || '';
  const ONLINE_SHIFT = SYSTEM?.ONLINE_SHIFT || '';
  const CURRENT_SHIFT = current_shift || '';
  const imageProfile = isEmpty(profile_pic) ? imageDefault : profile_pic;
  const history = useHistory();
  const dispatch = useDispatch();

  const { setOpenReleaseModal, hasReadCurrentRelease, hydrateReleaseForUser, setOpenAboutModal } =
    useAppStateStore();

  const email = authUser?.email ?? '';
  const prop_id = authUser?.prop_id ?? 0;

  useEffect(() => {
    if (email && prop_id) {
      hydrateReleaseForUser(email, prop_id);
    }
  }, [email, prop_id]);

  // ── Release period check ─────────────────────────────────────────
  // Get SYSTEM_DATE from Redux (matches the hotel's current date, not browser clock)
  const latestRelease = releaseData['en']?.[0];
  const releaseDate = latestRelease?.release_date || '';
  const badgeText = latestRelease?.version ? `v${latestRelease.version}` : 'v2.2.9';

  const isWithinReleasePeriod = (): boolean => {
    try {
      const currentRealDate = moment().startOf('day');
      const startDate = moment(RELEASE_START_DATE, 'YYYY-MM-DD').startOf('day');
      const expiryDate = startDate.clone().add(3, 'days');

      return currentRealDate.isSameOrBefore(expiryDate);
    } catch {
      return false;
    }
  };

  // Check if they've read it && within 2-day period based on system date
  const showNew = !hasReadCurrentRelease && isWithinReleasePeriod();

  // LOG for debugging:
  // console.log(
  //   `[UserInfo Render] hasReadCurrentRelease: ${hasReadCurrentRelease} -> showNew: ${showNew}`
  // );

  // ── Badge display: Top badge shows for 2.5s on mount, then DISAPPEARS ──────
  const [showTopBadge, setShowTopBadge] = useState(false);

  useEffect(() => {
    injectUserInfoStyles();
  }, []);

  useEffect(() => {
    if (showNew) {
      // Show badge initially
      setShowTopBadge(true);
      // Hide badge completely after 2 minutes (120,000 ms)
      const t = setTimeout(() => setShowTopBadge(false), 120000);
      return () => clearTimeout(t);
    } else {
      setShowTopBadge(false);
    }
  }, [showNew]);

  const onSignOut = () => {
    if (markLocations.includes(history.location.pathname)) {
      Modal.confirm({
        title: 'Confirm Sign out',
        content: 'Are you sure you want to sign out?',
        onOk: () => {
          setTimeout(() => {
            dispatch(userSignOut());
          }, 200);
        },
      });
    } else {
      dispatch(userSignOut());
    }
  };

  const userProfile = () => {
    if (markLocations.includes(history.location.pathname)) return;
    history.push('/main/userprofile');
  };

  const iconWrapperStyle: React.CSSProperties = {
    display: 'inline-flex',
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: '#1677ff',
    color: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const iconStyle = { fontSize: 12 };

  const versionIconStyle: React.CSSProperties = {
    ...iconWrapperStyle,
    backgroundColor: showNew ? '#52c41a' : '#1677ff',
    boxShadow: showNew ? '0 0 8px rgba(82,196,26,0.55)' : 'none',
    transition: 'all 0.3s ease',
  };

  const userMenuOptions = (
    <StyledUserMenu>
      <Menu.Item
        key='welcome'
        disabled
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faUser} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.Welcome' /> : {user_name}
      </Menu.Item>
      <Menu.Item
        key='currentShift'
        disabled
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faMinus} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.CurrentShift' /> : {CURRENT_SHIFT.toUpperCase()}
      </Menu.Item>
      {SYSTEM?.ONLINE_POST && (
        <Menu.Item
          key='onlineShift'
          disabled
          icon={
            <span style={iconWrapperStyle}>
              <FontAwesomeIcon icon={faMinus} style={iconStyle} />
            </span>
          }
        >
          <IntlMessages id='app.global.label.OnlineShift' /> :{' '}
          {ONLINE_SHIFT.toUpperCase() !== 'OFF' ? ONLINE_SHIFT.toUpperCase() : 'Offline'}
        </Menu.Item>
      )}
      <Menu.Item
        key='systemDate'
        disabled
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faCalendar} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.SystemDate' /> :{' '}
        {dayjs(SYSTEM_DATE).format('DD/MM/YYYY')}
      </Menu.Item>
      <Menu.Item
        key='login'
        disabled
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faCheckCircle} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.Login' />: {dayjs(login_time).format('DD/MM/YYYY HH:mm')}
      </Menu.Item>
      <Menu.Item
        key='profile'
        onClick={() => userProfile()}
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faKey} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.SetupProfile' />
      </Menu.Item>

      {/* ── What's New / Version — highlighted + blinks when unread ── */}
      <Menu.Item
        key='version'
        onClick={() => setOpenReleaseModal(true)}
        className={cn(showNew && 'ui-menu-version-new', blinking && 'ui-menu-version-blinking')}
        icon={
          <span className={blinking ? 'ui-icon-glow' : ''} style={versionIconStyle}>
            <FontAwesomeIcon icon={faNewspaper} style={iconStyle} />
          </span>
        }
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IntlMessages id='app.global.label.aboutThisVersion' />
          {showNew && (
            <span
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg,#52c41a,#73d13d)',
                color: '#fff',
                fontSize: '8px',
                fontWeight: 800,
                letterSpacing: '0.5px',
                padding: '1px 5px',
                borderRadius: '9999px',
                lineHeight: 1.4,
              }}
            >
              {badgeText}
            </span>
          )}
        </span>
      </Menu.Item>
      <Menu.Item
        key='about'
        onClick={() => setOpenAboutModal(true)}
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faInfoCircle} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.about' />
      </Menu.Item>

      <Menu.Item
        key='logout'
        onClick={() => onSignOut()}
        icon={
          <span style={iconWrapperStyle}>
            <FontAwesomeIcon icon={faSignOutAlt} style={iconStyle} />
          </span>
        }
      >
        <IntlMessages id='app.global.label.Logout' />
      </Menu.Item>
    </StyledUserMenu>
  );

  return (
    <Dropdown
      popupRender={() => userMenuOptions}
      placement='bottomRight'
      trigger={['click']}
      onOpenChange={(open: boolean) => setDropdownVisible(open)}
    >
      <div className={cn('relative', showTopBadge && 'ui-new-ring')}>
        {showTopBadge && <span className='ui-new-badge ui-badge-blinking'>{badgeText}</span>}

        {/* User button */}
        <div
          className={cn(
            'mt-1 md:mt-0 inline-flex items-center justify-center gap-1',
            'bg-[#e6f7ff] border border-[#91d5ff]',
            'py-[6px] px-[12px] rounded-full font-[600] text-sm text-[#1890ff]',
            'shadow-sm hover:shadow-md transition-all duration-300 ease-in-out',
            'group cursor-pointer hover:border-[#40a9ff]'
          )}
        >
          <Tooltip

          >
            <div className='relative flex-shrink-0 w-[24px] h-[24px] rounded-full overflow-hidden cursor-pointer transition-all duration-300 ease-in-out group-hover:scale-110 group-hover:shadow-md'>
              <img
                src={imageProfile}
                alt='User Avatar'
                className='w-full h-full rounded-full transition-all duration-300 ease-in-out'
                style={{ objectFit: 'contain', objectPosition: 'center', display: 'block' }}
              />
            </div>
          </Tooltip>
          <span className='hidden md:inline-block whitespace-nowrap'>{user_name}</span>
          <MdKeyboardArrowDown className='hidden md:inline-block text-[16px] text-[#40a9ff] group-hover:text-[#1677ff] group-hover:scale-110 transition-all duration-300 ease-in-out flex-shrink-0 -ml-[4px]' />
        </div>
      </div>
    </Dropdown>
  );
};

export default UserInfo;
